import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { RefreshCw, AlertTriangle, Clock, CheckCircle2, FileText, Send, Paperclip } from 'lucide-react';
import { puntovService } from '../../../services/api';
import { useAuth } from '../../../hooks/useAuth';
import TablaDatos from './TablaDatos';
import DialogoFactura from './DialogoFactura';
import { fmtUsd, fmtFecha, fmtFechaHora, fmtPeriodo, textoPlazoFactura } from './formato';

const DIAS_AVISO = 2;

const INSIGNIA = {
  vencida: { texto: 'Vencida', clase: 'text-red-700 bg-red-50 border-red-200', icono: AlertTriangle },
  pendiente: { texto: 'Pendiente', clase: 'text-amber-700 bg-amber-50 border-amber-200', icono: Clock },
  enviada: { texto: 'Enviada', clase: 'text-green-700 bg-green-50 border-green-200', icono: CheckCircle2 }
};

const claveSolicitud = (s) => s.id;
const textoBusquedaSolicitud = (s) =>
  `${s.numero} ${s.cliente || ''} ${s.empresa} ${s.cotizacion_numero || ''} ${fmtPeriodo(s.periodo)} ${s.factura_numero || ''}`;

const SolicitudesFactura = () => {
  const { user } = useAuth();
  const esAdmin = user?.rol === 'admin';

  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [abierta, setAbierta] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await puntovService.listarSolicitudesFactura();
      setSolicitudes(res.data || []);
    } catch (err) {
      setError(err.message || 'Error al cargar las solicitudes de factura');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  const vencidas = useMemo(() => solicitudes.filter(s => s.situacion === 'vencida'), [solicitudes]);
  const porVencer = useMemo(
    () => solicitudes.filter(s => s.situacion === 'pendiente' && Number(s.dias_restantes) <= DIAS_AVISO),
    [solicitudes]
  );

  const cerrar = useCallback(() => setAbierta(null), []);

  const columnas = useMemo(() => [
    {
      id: 'solicitud',
      titulo: 'Solicitud',
      ordenable: true,
      valor: (s) => s.solicitada_at,
      render: (s) => (
        <>
          <div className="font-medium text-gray-800 tabular-nums">{s.numero}</div>
          <div className="text-xs text-gray-400">{fmtFechaHora(s.solicitada_at)}</div>
        </>
      )
    },
    {
      id: 'cliente',
      titulo: 'Cliente',
      ordenable: true,
      valor: (s) => s.cliente || s.empresa,
      render: (s) => (
        <>
          <div className="text-gray-700">{s.cliente || '—'}</div>
          <div className="text-xs text-gray-400">{s.empresa.replace(/^esgamanc_/, '')}</div>
        </>
      )
    },
    {
      id: 'cotizacion',
      titulo: 'Cotización',
      ordenable: true,
      valor: (s) => s.cotizacion_numero || '',
      render: (s) => (
        <>
          <div className="text-gray-700 tabular-nums">{s.cotizacion_numero || '—'}</div>
          <div className="text-xs text-gray-400">Mensualidad {fmtPeriodo(s.periodo)}</div>
        </>
      )
    },
    {
      id: 'monto',
      titulo: 'Monto',
      ordenable: true,
      alinear: 'right',
      valor: (s) => Number(s.total_usd),
      render: (s) => <span className="tabular-nums text-gray-800">{fmtUsd(s.total_usd)}</span>
    },
    {
      id: 'plazo',
      titulo: 'Fecha límite',
      ordenable: true,
      valor: (s) => String(s.fecha_limite).slice(0, 10),
      render: (s) => (
        <>
          <div className="text-gray-700 tabular-nums">{fmtFecha(s.fecha_limite)}</div>
          {s.situacion !== 'enviada' && (
            <div className={`text-xs ${s.situacion === 'vencida' ? 'text-red-600 font-medium' : Number(s.dias_restantes) <= DIAS_AVISO ? 'text-amber-700' : 'text-gray-500'}`}>
              {textoPlazoFactura(s)}
            </div>
          )}
        </>
      )
    },
    {
      id: 'factura',
      titulo: 'Factura',
      valor: (s) => s.factura_numero || '',
      render: (s) => (s.factura_archivo
        ? (
          <div className="text-xs">
            <div className="flex items-center gap-1 text-gray-700"><FileText size={13} className="text-red-500" /> {s.factura_numero ? `N.º ${s.factura_numero}` : 'Adjunta'}</div>
            {s.enviado_at
              ? <div className="text-gray-400 truncate max-w-[12rem]" title={s.correo_enviado_a}>a {s.correo_enviado_a}</div>
              : s.error_envio
                ? <div className="text-red-600 truncate max-w-[12rem]" title={s.error_envio}>No se envió</div>
                : <div className="text-gray-400">Sin enviar</div>}
          </div>
        )
        : <span className="text-xs text-gray-400">Sin adjuntar</span>)
    },
    {
      id: 'situacion',
      titulo: 'Estado',
      ordenable: true,
      valor: (s) => s.situacion,
      filtro: [
        { valor: 'vencida', etiqueta: 'Vencida' },
        { valor: 'pendiente', etiqueta: 'Pendiente' },
        { valor: 'enviada', etiqueta: 'Enviada' }
      ],
      render: (s) => {
        const i = INSIGNIA[s.situacion];
        return (
          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap ${i.clase}`}>
            <i.icono size={12} /> {i.texto}
          </span>
        );
      }
    },
    ...(esAdmin ? [{
      id: 'acciones',
      titulo: '',
      alinear: 'right',
      valor: () => '',
      render: (s) => (
        <button
          type="button"
          onClick={() => setAbierta(s)}
          className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-lg border whitespace-nowrap ${
            s.situacion === 'vencida'
              ? 'border-red-200 text-red-700 hover:bg-red-50'
              : 'border-gray-200 text-gray-600 hover:border-blue-300 hover:text-blue-700'
          }`}
        >
          {s.situacion === 'enviada' ? <><Send size={12} /> Reenviar</> : s.factura_archivo ? <><Send size={12} /> Enviar</> : <><Paperclip size={12} /> Adjuntar y enviar</>}
        </button>
      )
    }] : [])
  ], [esAdmin]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <p className="text-sm text-gray-500">
          Facturas que los clientes pidieron desde Punto V. Hay 5 días hábiles para enviarlas.
        </p>
        <button
          onClick={cargar}
          disabled={loading}
          className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 flex items-center gap-2 text-sm disabled:opacity-60 self-start sm:self-auto"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Actualizar
        </button>
      </div>

      {vencidas.length > 0 && (
        <div role="alert" className="bg-red-50 border border-red-200 text-red-800 rounded-xl p-4 text-sm flex items-start gap-3">
          <AlertTriangle size={18} className="mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">
              {vencidas.length === 1 ? '1 factura pasó la fecha límite' : `${vencidas.length} facturas pasaron la fecha límite`}
            </p>
            <ul className="mt-1 space-y-0.5">
              {vencidas.map(s => (
                <li key={s.id}>
                  {esAdmin ? (
                    <button type="button" onClick={() => setAbierta(s)} className="underline underline-offset-2 hover:text-red-900">
                      {s.cliente || s.empresa} · {s.cotizacion_numero} · {textoPlazoFactura(s).toLowerCase()}
                    </button>
                  ) : `${s.cliente || s.empresa} · ${s.cotizacion_numero} · ${textoPlazoFactura(s).toLowerCase()}`}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {porVencer.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-xl p-4 text-sm flex items-start gap-3">
          <Clock size={18} className="mt-0.5 flex-shrink-0" />
          <p>
            {porVencer.length === 1 ? '1 factura vence' : `${porVencer.length} facturas vencen`} en {DIAS_AVISO} días o menos:
            {' '}{porVencer.map(s => `${s.cliente || s.empresa} (${fmtFecha(s.fecha_limite)})`).join(', ')}.
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" /> <span>{error}</span>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {loading && solicitudes.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : (
          <TablaDatos
            columnas={columnas}
            filas={solicitudes}
            clave={claveSolicitud}
            textoBusqueda={textoBusquedaSolicitud}
            placeholderBusqueda="Buscar cliente, cotización o número"
            vacio="Ningún cliente ha solicitado factura todavía"
          />
        )}
      </div>

      {abierta && <DialogoFactura solicitud={abierta} onClose={cerrar} onCambio={cargar} />}
    </div>
  );
};

export default SolicitudesFactura;
